/**
 * ─────────────────────────────────────────────────────────────
 * TEMPORARY: monolith routing shim (2026-09-30)
 * ─────────────────────────────────────────────────────────────
 *
 * The backend collapsed all per-service subdomains
 * (authentication.domainboost.io, wallet.domainboost.io, …) into a
 * single `api.domainboost.io/<service>/…` mount. The old subdomains
 * now serve HTTP 308 redirects, which browsers refuse to follow
 * for cross-origin POST requests — breaking login and every other
 * mutation the FE issues.
 *
 * Rather than edit every GitHub secret / workflow / .env, this
 * module exports a pure URL rewriter. The rewriter turns anything
 * shaped `https://<svc>.domainboost.io[/…]` (where <svc> is in the
 * known-service allowlist) into `https://api.domainboost.io/<svc>[/…]`.
 * Non-matching URLs pass through unchanged.
 *
 * The toggle itself is env-driven: `VITE_USE_MONOLITH_ROUTING`.
 * See `readMonolithFlag` below for the parsing rule.
 *
 * Consumers:
 *   - `src/config/env.ts` — reads the flag from `import.meta.env`
 *     and wraps every `VITE_*_API_URL` read.
 *   - `vite.config.ts` — reads the flag from `loadEnv(mode, …)`
 *     and uses the same helpers for the dev-server proxy.
 *
 * To revert (backend rolls back to per-service subdomains):
 *   set `VITE_USE_MONOLITH_ROUTING=false` in the env. No code
 *   change needed.
 */

export const MONOLITH_HOST = 'https://api.domainboost.io';

/** Service subdomains that have been consolidated into the monolith. */
export const MONOLITH_SERVICES = [
  'authentication',
  'user',
  'master',
  'site',
  'wallet',
  'order',
  'task',
  'support',
  'notification',
  'publisher',
  'seo',
] as const;

const SERVICE_PATTERN = new RegExp(`^https://(${MONOLITH_SERVICES.join('|')})\\.domainboost\\.io`);

/**
 * Parse the `VITE_USE_MONOLITH_ROUTING` flag from an env bag.
 * Enabled unless the value is literally the string "false".
 * Missing/empty defaults to enabled so the live server keeps its
 * current behavior without any env var change.
 *
 * Accepts both the browser env (`import.meta.env`, values are
 * typed `any` but always strings/booleans at runtime) and the
 * Node env from `loadEnv()` (`Record<string, string>`).
 */
export const readMonolithFlag = (env: Record<string, string | boolean | undefined>): boolean =>
  env.VITE_USE_MONOLITH_ROUTING !== 'false';

/**
 * Rewrite a full API URL from the old per-service subdomain shape
 * to the new monolith shape. Idempotent — already-rewritten or
 * unrelated URLs pass through unchanged. `enabled` is passed
 * explicitly so the same helper works in the browser bundle
 * (reading `import.meta.env`) and in `vite.config.ts` (reading
 * `loadEnv`).
 *
 *   https://authentication.domainboost.io/api  →  https://api.domainboost.io/authentication/api
 *   https://wallet.domainboost.io              →  https://api.domainboost.io/wallet
 *   https://domainboost.io                     →  https://domainboost.io    (untouched)
 *   /api/auth                                  →  /api/auth                 (untouched — dev proxy path)
 *   undefined                                  →  undefined
 */
export const rewriteApiUrl = (url: string | undefined, enabled: boolean): string | undefined => {
  if (!url || !enabled) return url;
  return url.replace(SERVICE_PATTERN, (_m, svc: string) => `${MONOLITH_HOST}/${svc}`);
};
